// const API_BASE_URL = 'https://vault-scooter-builder--development.gadget.app';
const API_BASE_URL = 'https://vault-scooter-builder.gadget.app';
const BUILDER_PART_ATTRIBUTE = '_builder_part';
const BUNDLE_ATTRIBUTE = '_Bundle';

// ─────────────────────────────────────────────────────────────────────────────
// BuilderProductCard
// One card per product. Resolves the active variant from an embedded JSON map of
// available variants. Color is chosen via swatches; every other option becomes a
// <select> that JS rebuilds (showing only reachable values) as the color or an
// earlier option changes. #variant is the resolved variant and the source of
// truth for builder-card:select.
// ─────────────────────────────────────────────────────────────────────────────
class BuilderProductCard extends HTMLElement {
  #variant       = null;
  #variants      = [];
  #optionNames   = [];
  #colorIndex    = -1;
  #optionIndexes = [];   // non-color option indexes, in option order
  #optionValues  = [];   // all distinct values per option index (available variants)
  #selected      = [];   // currently selected value per option index
  #optionsZone   = null;

  connectedCallback() {
    this.#parseData();
    if (!this.#variants.length) return;

    this.#selected   = [...this.#variants[0].options];
    this.#optionsZone = this.querySelector('.js-builder-card-options');

    this.querySelector('.builder-card__swatches')
      ?.addEventListener('click', (e) => {
        const swatch = e.target.closest('.builder-card__swatch');
        if (swatch) this.#onSwatch(swatch);
      });

    this.#buildSelects();
    this.#optionsZone?.addEventListener('change', (e) => {
      const select = e.target.closest('.builder-card__option-select');
      if (select) this.#onSelect(Number(select.dataset.optionIndex), select.value);
    });

    this.#cascade();
    this.#resolve();
    this.#syncUI();

    this.querySelector('.js-builder-card-add')
      ?.addEventListener('click', () => this.#emit());
  }

  #parseData() {
    try {
      this.#variants = JSON.parse(
        this.querySelector('.js-builder-card-variants')?.textContent || '[]'
      );
      this.#optionNames = JSON.parse(this.dataset.optionNames || '[]');
    } catch {
      this.#variants = [];
    }
    this.#colorIndex = Number(this.dataset.colorIndex ?? -1);
    const count = this.#variants[0]?.options.length ?? 0;
    for (let i = 0; i < count; i++) {
      const values = [];
      for (const v of this.#variants) {
        if (!values.includes(v.options[i])) values.push(v.options[i]);
      }
      this.#optionValues[i] = values;
      if (i !== this.#colorIndex) this.#optionIndexes.push(i);
    }
  }

  // Creates one (initially empty) labelled <select> per non-color option.
  // #cascade fills the options and hides rows that have nothing to choose.
  #buildSelects() {
    if (!this.#optionsZone) return;
    for (const i of this.#optionIndexes) {
      const row = document.createElement('div');
      row.className = 'builder-card__option';
      row.dataset.optionIndex = String(i);

      const name = this.#optionNames[i] ?? '';
      if (name) {
        const label = document.createElement('span');
        label.className = 'builder-card__option-label';
        label.textContent = name;
        row.appendChild(label);
      }

      const select = document.createElement('select');
      select.className = 'builder-select builder-card__option-select';
      select.dataset.optionIndex = String(i);
      if (name) select.setAttribute('aria-label', name);
      row.appendChild(select);

      this.#optionsZone.appendChild(row);
    }
  }

  // Distinct values for one option among variants that match the already-fixed dims.
  #reachableValues(optIndex, fixedIndexes) {
    const values = [];
    for (const v of this.#variants) {
      if (!fixedIndexes.every(idx => v.options[idx] === this.#selected[idx])) continue;
      const value = v.options[optIndex];
      if (!values.includes(value)) values.push(value);
    }
    return values;
  }

  // Left-to-right: color is fixed first, then each non-color option is constrained
  // by the color + earlier options. Snaps a now-invalid selection to the first
  // reachable value so a valid variant is always selectable.
  #cascade() {
    const fixed = this.#colorIndex >= 0 ? [this.#colorIndex] : [];
    for (const i of this.#optionIndexes) {
      const reachable = this.#reachableValues(i, fixed);
      if (!reachable.includes(this.#selected[i])) this.#selected[i] = reachable[0];
      this.#renderSelect(i, reachable);
      fixed.push(i);
    }
    if (this.#optionsZone) {
      this.#optionsZone.hidden =
        ![...this.#optionsZone.children].some(s => !s.hidden);
    }
  }

  // Renders every value the option can take; values that aren't reachable for the
  // current selection are shown but disabled (clearer than removing them).
  #renderSelect(optIndex, reachable) {
    const select = this.#optionsZone?.querySelector(
      `.builder-card__option-select[data-option-index="${optIndex}"]`
    );
    if (!select) return;
    const all = this.#optionValues[optIndex];
    select.replaceChildren(...all.map(value => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = value;
      opt.disabled = !reachable.includes(value);
      return opt;
    }));
    select.value = this.#selected[optIndex];
    // A single possible value isn't a choice — hide the whole row.
    const row = select.closest('.builder-card__option');
    if (row) row.hidden = all.length <= 1;
  }

  #onSwatch(swatch) {
    this.#selected[this.#colorIndex] = swatch.dataset.optionValue;
    this.#cascade();
    this.#resolve();
    this.#syncUI();
  }

  #onSelect(optIndex, value) {
    this.#selected[optIndex] = value;
    this.#cascade();
    this.#resolve();
    this.#syncUI();
  }

  #resolve() {
    this.#variant = this.#variants.find(v =>
      v.options.every((opt, i) => opt === this.#selected[i])
    ) ?? this.#variants[0];
  }

  #syncUI() {
    const v = this.#variant;
    if (!v) return;

    this.querySelectorAll('.builder-card__swatch').forEach(s =>
      s.classList.toggle('is-active', s.dataset.optionValue === this.#selected[this.#colorIndex])
    );

    const img = this.querySelector('.js-builder-card-image');
    if (img && v.image) img.src = v.image;

    const variantEl = this.querySelector('.js-builder-card-variant');
    if (variantEl) variantEl.textContent = v.title === 'Default Title' ? '' : v.title;

    const priceWasEl = this.querySelector('.js-builder-card-price-was');
    if (priceWasEl) priceWasEl.textContent = v.priceWas;

    const priceNowEl = this.querySelector('.js-builder-card-price-now');
    if (priceNowEl) priceNowEl.textContent = v.priceNow;
  }

  #emit() {
    const v = this.#variant;
    if (!v) return;
    this.dispatchEvent(new CustomEvent('builder-card:select', {
      bubbles: true,
      detail: {
        variantId:    String(v.id),
        price:        v.price,
        weight:       v.weight || 0,
        // Single-variant products have a "Default Title" variant — don't append it.
        title:        v.title && v.title !== 'Default Title'
          ? `${this.dataset.productTitle} - ${v.title}`
          : this.dataset.productTitle,
        image:        v.image,
        builderImage: v.builderImage || null,
        barCut:       v.barCut || null,
        available:    true,
      },
    }));
  }
}

customElements.define('builder-product-card', BuilderProductCard);

// ─────────────────────────────────────────────────────────────────────────────
// BuilderModal (base)
// Shared overlay mechanics: scroll lock, focus trap, close button, Escape key.
// Not registered with customElements — only its concrete subclasses are.
// ─────────────────────────────────────────────────────────────────────────────
class BuilderModal extends HTMLElement {
  #scrollY     = 0;
  #opener      = null;
  #trapKeydown = null;

  connectedCallback() {
    this.querySelector('.js-builder-modal-close')
      ?.addEventListener('click', () => this.close());
    this.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  // Subclasses call this from their own open() to handle shared boilerplate.
  _activate(opener) {
    this.#opener  = opener;
    this.#scrollY = window.scrollY;
    document.body.classList.add('fixed');
    document.body.style.top = `-${this.#scrollY}px`;
    this.setAttribute('open', '');
    this.removeAttribute('aria-hidden');
    this.#trapFocus();
  }

  close() {
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.top = '';
    document.body.classList.remove('fixed');
    window.scrollTo(0, this.#scrollY);
    this.#removeTrapFocus();
    this.#opener?.focus();
  }

  #trapFocus() {
    const focusable = Array.from(this.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    this.#trapKeydown = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
      if (e.key === 'Escape') this.close();
    };

    document.addEventListener('keydown', this.#trapKeydown);
    first?.focus();
  }

  #removeTrapFocus() {
    if (this.#trapKeydown) {
      document.removeEventListener('keydown', this.#trapKeydown);
      this.#trapKeydown = null;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// BuilderPartModal
// Full-screen selection modal. Opens for a given part, fetches its collection
// cards via the Section Rendering API, and bubbles a selection event up to the
// parent ScooterBuilder when a card is picked.
// ─────────────────────────────────────────────────────────────────────────────
class BuilderPartModal extends BuilderModal {
  #blockId       = null;
  #abortCtrl     = null;
  #currentHandle = null;
  #filterParams  = new URLSearchParams();
  #searchQuery   = '';
  #onSearchInput = debounce((value) => {
    this.#searchQuery = value.trim().toLowerCase();
    this.#filterCards();
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    // Card selected inside the modal — re-bubble with blockId then close
    this.addEventListener('builder-card:select', (e) => {
      this.dispatchEvent(new CustomEvent('builder-modal:select', {
        bubbles: true,
        detail: { blockId: this.#blockId, ...e.detail },
      }));
      this.close();
    });
    // Filter select changes — delegated because selects are injected dynamically
    this.addEventListener('change', (e) => {
      const select = e.target.closest('.builder-filter-select');
      if (!select) return;
      this.#applyFilter(select);
    });

    // Search input — debounced, sets/clears q param and re-fetches
    this.addEventListener('input', (e) => {
      const input = e.target.closest('.js-builder-search-input');
      if (!input) return;
      this.#onSearchInput(input.value);
    });

    // Refinements accordion toggle (mobile) — controls search + filters together
    this.addEventListener('click', (e) => {
      if (!e.target.closest('.js-builder-filters-toggle')) return;
      const refinements = this.querySelector('.js-builder-modal-refinements');
      if (!refinements) return;
      const open = refinements.classList.toggle('is-open');
      e.target.closest('.js-builder-filters-toggle').setAttribute('aria-expanded', String(open));
    });

    // Mobile: tapping or dragging anything outside the floating filter panel
    // closes it. touchmove fires at the start of any drag (even at a scroll
    // boundary where `scroll` wouldn't), and its target is where the touch began.
    this.addEventListener('click', (e) => {
      if (e.target.closest('.js-builder-modal-refinements')) return;
      this.#closeRefinements();
    });
    this.addEventListener('touchmove', (e) => {
      if (e.target.closest('.js-builder-modal-refinements')) return;
      this.#closeRefinements();
    }, { passive: true });

    // Clear-all-filters button
    this.addEventListener('click', (e) => {
      if (!e.target.closest('.js-builder-filters-reset')) return;
      this.#resetFilters();
    });
  }

  // Collapses the mobile filter panel (no-op when already closed).
  #closeRefinements() {
    const refinements = this.querySelector('.js-builder-modal-refinements');
    if (!refinements?.classList.contains('is-open')) return;
    refinements.classList.remove('is-open');
    refinements.querySelector('.js-builder-filters-toggle')
      ?.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {string} blockId
   * @param {string} partName   - Display label e.g. "Deck"
   * @param {string} handle     - Collection handle to fetch
   * @param {Element} opener    - The trigger button (for focus restoration)
   */
  open(blockId, partName, handle, opener) {
    this.#blockId       = blockId;
    this.#currentHandle = handle;
    this.#filterParams  = new URLSearchParams();

    this.querySelector('.js-builder-modal-part-name').textContent = partName.toUpperCase();

    this.#searchQuery = '';
    this.querySelector('.js-builder-search-input').value = '';
    this.querySelector('.js-builder-modal-filters').innerHTML = '';
    this.querySelector('.js-builder-modal-cards').innerHTML = '<p class="builder-modal__status">Loading…</p>';
    this.#syncFiltersReset();

    this._activate(opener);
    this.#fetchCards(handle);
  }

  close() {
    this.#abortCtrl?.abort();
    super.close();
  }

  #applyFilter(select) {
    const param = select.dataset.filterParam;
    const value = select.value;

    // Replace any existing value for this param (selects are single-value per group)
    const remaining = [...this.#filterParams.entries()].filter(([k]) => k !== param);
    this.#filterParams = new URLSearchParams(remaining);
    if (value) this.#filterParams.append(param, value);

    this.#syncFiltersReset();
    this.#fetchCards(this.#currentHandle);
  }

  #resetFilters() {
    this.#filterParams = new URLSearchParams();
    this.#syncFiltersReset();
    this.#fetchCards(this.#currentHandle);
  }

  // Shows the "Clear" button only while at least one filter is applied.
  #syncFiltersReset() {
    const btn = this.querySelector('.js-builder-filters-reset');
    if (btn) btn.hidden = [...this.#filterParams].length === 0;
  }

  #filterCards() {
    const zone = this.querySelector('.js-builder-modal-cards');
    if (!zone) return;
    const q = this.#searchQuery;
    const cards = zone.querySelectorAll('builder-product-card');
    let visible = 0;
    cards.forEach((card) => {
      const match = !q || (card.dataset.productTitle ?? '').toLowerCase().includes(q);
      card.hidden = !match;
      if (match) visible++;
    });
    let noResults = zone.querySelector('.js-builder-search-no-results');
    if (visible === 0 && cards.length > 0) {
      if (!noResults) {
        noResults = Object.assign(document.createElement('p'), {
          className: 'builder-modal__status js-builder-search-no-results',
          textContent: 'No products match your search.',
        });
        zone.appendChild(noResults);
      }
    } else {
      noResults?.remove();
    }
  }

  async #fetchCards(handle) {
    this.#abortCtrl?.abort();
    this.#abortCtrl = new AbortController();

    const cardsZone   = this.querySelector('.js-builder-modal-cards');
    const filtersZone = this.querySelector('.js-builder-modal-filters');

    this.classList.add('is-loading');
    cardsZone.innerHTML = '<p class="builder-modal__status">Loading…</p>';

    try {
      // Always constrain to in-stock variants so out-of-stock filter values come
      // back with count 0 and render disabled. Use a local copy so the availability
      // param never leaks into #filterParams (keeps the reset button + per-group
      // logic reasoning only about user-selected filters). Value must be 1/0/0,1.
      const params = new URLSearchParams(this.#filterParams);
      params.set('filter.v.availability', '1');
      params.set('section_id', 'collection-scooter-builder-cards');
      const url = `/collections/${handle}?${params.toString()}`;
      const res = await fetch(url, { signal: this.#abortCtrl.signal });
      if (!res.ok) throw new Error(res.statusText);

      const html    = await res.text();
      const doc     = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.section-scooter-builder-cards');

      const newFilters = section?.querySelector('#collection-scooter-builder-filters');
      const newCards   = section?.querySelector('#collection-scooter-builder-cards');

      filtersZone.innerHTML = newFilters?.innerHTML ?? '';

      cardsZone.innerHTML = newCards?.innerHTML.trim()
        ? newCards.innerHTML
        : '<p class="builder-modal__status">No products found.</p>';
      if (this.#searchQuery) this.#filterCards();
    } catch (err) {
      if (err.name === 'AbortError') return;
      cardsZone.innerHTML = '<p class="builder-modal__status builder-modal__status--error">Could not load products. Please try again.</p>';
    } finally {
      this.classList.remove('is-loading');
    }
  }
}

customElements.define('builder-part-modal', BuilderPartModal);

// ─────────────────────────────────────────────────────────────────────────────
// BuilderShareModal
// Creates a new build via the Gadget API, then shows the customId and a
// copy-link button so the user can share the current selection.
// ─────────────────────────────────────────────────────────────────────────────
class BuilderShareModal extends BuilderModal {
  #abortCtrl   = null;
  #lastBuild   = null;
  #customerId  = undefined;
  #customId    = '';
  #shareLink   = '';
  #copyLabel   = '';

  connectedCallback() {
    super.connectedCallback();
    this.#customerId = this.dataset.customerId || undefined;

    const copyBtn = this.querySelector('.js-builder-share-copy');
    if (copyBtn) {
      this.#copyLabel = copyBtn.textContent.trim();
      copyBtn.addEventListener('click', () => this.#copy());
    }

    // Auto add-to-cart toggle — rebuilds the link with/without ?atc=true
    this.querySelector('.js-builder-share-atc')
      ?.addEventListener('change', () => this.#updateShareLink());

    this.querySelector('.js-builder-share-retry')
      ?.addEventListener('click', () => this.#createBuild(this.#lastBuild));
  }

  open(build, opener, canAutoAtc = true) {
    this.#lastBuild = build;

    // Hide the "add to cart when opened" toggle for builds that aren't cart-ready,
    // and force it off so the generated link never carries atc=true in that case.
    const atcRow   = this.querySelector('.builder-share__atc');
    const atcInput = this.querySelector('.js-builder-share-atc');
    if (atcRow) atcRow.hidden = !canAutoAtc;
    if (atcInput) atcInput.checked = canAutoAtc;

    this._activate(opener);
    this.#createBuild(build);
  }

  close() {
    this.#abortCtrl?.abort();
    super.close();
  }

  async #createBuild({ parts, addOns, barCuts }) {
    this.#showState('loading');
    this.#abortCtrl?.abort();
    this.#abortCtrl = new AbortController();
    try {
      const body = { parts, addOns, barCuts, domain: window.Shopify?.shop ?? '' };
      if (this.#customerId) body.customerId = this.#customerId;

      const res = await fetch(
        `${API_BASE_URL}/builds`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: this.#abortCtrl.signal,
        }
      );
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const json = await res.json();
      this.#renderResult(json.customId);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[BuilderShareModal] Failed to create build:', err);
      this.#showState('error');
    }
  }

  #renderResult(customId) {
    this.#customId = customId;
    this.querySelector('.js-builder-share-id').textContent = customId;
    this.#updateShareLink();
    this.#showState('result');
  }

  // Rebuilds #shareLink from the current customId + the auto-ATC checkbox state.
  #updateShareLink() {
    const wantsAtc = this.querySelector('.js-builder-share-atc')?.checked;
    this.#shareLink = `${location.origin}${location.pathname}?buildId=${encodeURIComponent(this.#customId)}`;
    if (wantsAtc) this.#shareLink += '&atc=true';
    const input = this.querySelector('.js-builder-share-link');
    if (input) input.value = this.#shareLink;
  }

  async #copy() {
    const btn = this.querySelector('.js-builder-share-copy');
    try {
      await navigator.clipboard.writeText(this.#shareLink);
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = this.#copyLabel; }, 2000);
    } catch {
      this.querySelector('.js-builder-share-link')?.select();
    }
  }

  #showState(name) {
    this.querySelector('.js-builder-share-loading').hidden = name !== 'loading';
    this.querySelector('.js-builder-share-result').hidden  = name !== 'result';
    this.querySelector('.js-builder-share-error').hidden   = name !== 'error';
  }
}

customElements.define('builder-share-modal', BuilderShareModal);

// ─────────────────────────────────────────────────────────────────────────────
// BuilderPartBarCutsModal
// Width/height cut selection for the currently-selected linked part (Bars). The
// options are baked into the markup from two fixed products (via the metaobject,
// same source as snippets/custom-add-ons.liquid). This component only filters
// them to the selected bar's min/max bounds, hides a dimension the bar doesn't
// allow, and emits the chosen cut variant(s) back to ScooterBuilder.
// ─────────────────────────────────────────────────────────────────────────────
class BuilderPartBarCutsModal extends BuilderModal {
  #linkedPart = null;

  connectedCallback() {
    super.connectedCallback();
    this.querySelector('.js-builder-bar-cuts-confirm')
      ?.addEventListener('click', () => this.#confirm());

    // Cache the full, baked-in option set for each dimension select. Safari's
    // native picker ignores `hidden`/`disabled` on <option>, so we can't filter
    // by toggling — we rebuild the live <select> from this cache on every open.
    this.querySelectorAll('.js-builder-bar-cuts-select').forEach((select) => {
      select._allOptions = [...select.options].map((opt) => opt.cloneNode(true));
    });
  }

  /**
   * @param {object}  args
   * @param {string}  args.linkedPart - Part name these cuts belong to
   * @param {object}  args.bounds     - { widthEnabled, widthMin, widthMax, heightEnabled, heightMin, heightMax, slitEnabled }
   * @param {object}  args.selection  - { width: variantId|null, height: variantId|null, slit: boolean }
   * @param {object}  args.labels     - { title, confirm, none, empty }
   * @param {Element} args.opener     - Trigger (for focus restoration)
   */
  open({ linkedPart, bounds, selection, labels, opener }) {
    this.#linkedPart = linkedPart;

    this.querySelector('.js-builder-bar-cuts-title').textContent = labels.title || '';
    const confirmBtn = this.querySelector('.js-builder-bar-cuts-confirm');
    if (confirmBtn) confirmBtn.textContent = labels.confirm || '';

    let anyVisible = false;
    this.querySelectorAll('.js-builder-bar-cuts-group').forEach((group) => {
      anyVisible = this.#prepareGroup(group, bounds, selection) || anyVisible;
    });
    anyVisible = this.#prepareSlit(bounds, selection) || anyVisible;

    // Applied after #prepareGroup rebuilds each select from cache, so the fresh
    // "no cut" option (a clone of the un-labelled cached node) gets its text.
    this.querySelectorAll('.js-builder-bar-cuts-none').forEach((opt) => {
      opt.textContent = labels.none || '';
    });

    const empty = this.querySelector('.js-builder-bar-cuts-empty');
    if (empty) {
      empty.textContent = labels.empty || '';
      empty.hidden = anyVisible;
    }
    if (confirmBtn) confirmBtn.hidden = !anyVisible;

    this._activate(opener);
  }

  // Rebuilds one dimension's <select> from the cached option set, keeping only
  // options within [min, max] (plus the "no cut" option). We rebuild rather than
  // hide because Safari's native picker still shows hidden/disabled <option>s.
  // Hides the group when the bar disallows the dimension or nothing fits.
  #prepareGroup(group, bounds, selection) {
    const dim     = group.dataset.dimension;
    const enabled = bounds[`${dim}Enabled`];
    const min     = Number(bounds[`${dim}Min`] ?? 0);
    const max     = Number(bounds[`${dim}Max`] ?? Infinity);
    const select  = group.querySelector('.js-builder-bar-cuts-select');
    const all     = select?._allOptions;

    if (!enabled || !all) {
      group.hidden = true;
      return false;
    }

    let fits = 0;
    const options = all.filter((opt) => {
      if (!opt.value) return true; // the "no cut" option always stays selectable
      const size = Number(opt.dataset.size);
      const ok   = size >= min && size <= max;
      if (ok) fits++;
      return ok;
    });
    select.replaceChildren(...options.map((opt) => opt.cloneNode(true)));

    // Restore the previous choice when it's still in range, else fall back to "no cut".
    const want   = selection?.[dim] || '';
    const wanted = want ? select.querySelector(`option[value="${want}"]`) : null;
    select.value = wanted ? want : '';

    group.hidden = fits === 0;
    return fits > 0;
  }

  // Shows the optional "install a slit" checkbox only when the selected bar's
  // metafield allows it. Unlike #prepareGroup there is no size filtering — the
  // slit is a single fixed product. Returns whether the group is visible.
  #prepareSlit(bounds, selection) {
    const group = this.querySelector('.js-builder-bar-cuts-slit-group');
    if (!group) return false;
    const checkbox = group.querySelector('.js-builder-bar-cuts-slit');
    const enabled  = !!bounds.slitEnabled && !!checkbox;
    group.hidden = !enabled;
    if (checkbox) checkbox.checked = enabled && !!selection?.slit;
    return enabled;
  }

  #confirm() {
    const detail = { linkedPart: this.#linkedPart, cuts: {} };
    this.querySelectorAll('.js-builder-bar-cuts-group').forEach((group) => {
      if (group.hidden) return;
      const dim    = group.dataset.dimension;
      const select = group.querySelector('.js-builder-bar-cuts-select');
      const opt    = select?.selectedOptions[0];
      detail.cuts[dim] = opt && opt.value
        ? {
            dimension: dim,
            variantId: opt.value,
            price:     Number(opt.dataset.price)  || 0,
            weight:    Number(opt.dataset.weight) || 0,
            title:     opt.dataset.title || '',
          }
        : null;
    });

    const slitGroup = this.querySelector('.js-builder-bar-cuts-slit-group');
    const slitBox   = slitGroup?.querySelector('.js-builder-bar-cuts-slit');
    detail.cuts.slit = slitGroup && !slitGroup.hidden && slitBox?.checked
      ? {
          variantId: slitBox.dataset.variantId,
          price:     Number(slitBox.dataset.price)  || 0,
          weight:    Number(slitBox.dataset.weight) || 0,
          title:     slitBox.dataset.title || '',
        }
      : null;

    this.dispatchEvent(new CustomEvent('builder-bar-cuts:select', { bubbles: true, detail }));
    this.close();
  }
}

customElements.define('builder-part-bar-cuts-modal', BuilderPartBarCutsModal);

// ─────────────────────────────────────────────────────────────────────────────
// ScooterBuilder
// Root web component. Owns all state, wires up part-card interactions, manages
// the progress bar / totals, and submits the bundle to /cart/add.js.
// ─────────────────────────────────────────────────────────────────────────────
class ScooterBuilder extends HTMLElement {
  // blockId → { variantId, price, weight, title, image, builderImage, barCut }
  #state = new Map();
  // linkedPart → { width: cut|null, height: cut|null, slit: cut|null }, where a
  // width/height cut is { dimension, variantId, price, weight, title } and the
  // slit cut is { variantId, price, weight, title } (a single fixed product).
  #barCuts = new Map();

  connectedCallback() {
    // Defer init by one animation frame so BuilderPartModal has had a chance
    // to teleport itself to document.body before we look it up by ID.
    requestAnimationFrame(() => this.#init());
  }

  #init() {
    this._modal         = document.getElementById(`builder-modal-${this.dataset.sectionId}`);
    this._shareModal    = document.getElementById(`builder-share-modal-${this.dataset.sectionId}`);
    this._barCutsModal  = document.getElementById(`builder-bar-cuts-modal-${this.dataset.sectionId}`);
    this._fill       = this.querySelector('.js-builder-progress-fill');
    this._count      = this.querySelector('.js-builder-selected-count');
    this._weight     = this.querySelector('.js-builder-total-weight');
    this._priceWas   = this.querySelector('.js-builder-total-price-was');
    this._price      = this.querySelector('.js-builder-total-price');
    this._atcBtn     = this.querySelector('.js-builder-atc');
    this._track      = this.querySelector('.scooter-builder__progress-track');
    this._preview    = this.querySelector('.js-builder-preview');
    this._loading    = this.querySelector('.js-builder-loading');

    // Part triggers
    this.querySelectorAll('.js-builder-part-trigger').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const part = e.currentTarget.closest('.js-builder-part');
        this.#openModal(part, e.currentTarget);
      });
    });

    // Remove buttons
    this.querySelectorAll('.js-builder-part-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const part = e.currentTarget.closest('.js-builder-part');
        this.#removePart(part);
      });
    });

    // Modal selection — dispatched by BuilderPartModal and bubbles up
    this.addEventListener('builder-modal:select', (e) => this.#onSelect(e.detail));

    // Bar-cuts card triggers (locked until their linked part is selected)
    this.querySelectorAll('.js-builder-bar-cuts-trigger').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const card = e.currentTarget.closest('.js-builder-bar-cuts-card');
        this.#openBarCutsModal(card, e.currentTarget);
      });
    });

    // Bar-cuts remove buttons
    this.querySelectorAll('.js-builder-bar-cuts-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = e.currentTarget.closest('.js-builder-bar-cuts-card');
        this.#clearBarCuts(card);
      });
    });

    // Bar-cuts selection — dispatched by BuilderPartBarCutsModal and bubbles up
    this.addEventListener('builder-bar-cuts:select', (e) => this.#onBarCutsSelect(e.detail));

    // Add-on checkboxes
    this.querySelectorAll('.js-builder-addon-checkbox').forEach((cb) => {
      cb.addEventListener('change', (e) => this.#toggleAddon(e.currentTarget));
    });

    // Add to Cart
    this._atcBtn.addEventListener('click', () => this.#addToCart());

    // Save image
    this.querySelector('.js-builder-save')
      ?.addEventListener('click', (e) => this.#savePreviewImage(e.currentTarget));

    // Share
    this.querySelector('.js-builder-share')
      ?.addEventListener('click', (e) => this.#openShare(e.currentTarget));

    // Reset
    this.querySelector('.js-builder-reset')
      ?.addEventListener('click', () => this.#reset());

    const buildId = new URLSearchParams(location.search).get('buildId');
    if (buildId) {
      this.#loadFromApi(buildId);
    } else {
      this.#restoreFromStorage();
      this.#syncUI();
    }
  }

  // ── Private ───────────────────────────────────────────────────────────────

  #openModal(part, opener) {
    const blockId  = part.dataset.partName;
    const handle   = part.dataset.collection;
    const partName = part.querySelector('.builder-part__name')?.textContent.trim() ?? '';
    this._modal.open(blockId, partName, handle, opener);
  }

  #openShare(opener) {
    const parts = Object.fromEntries(
      [...this.#state].map(([blockId, { variantId }]) => [blockId, variantId])
    );
    const addOns = this.#selectedAddonRows().map((el) => el.dataset.variantId);

    // Bar cuts, keyed by linked part. Width/height are sent as bare ids (like
    // `parts`) and the API re-enriches them on GET; the slit is a single fixed
    // product, so it's just a boolean flag — the client rebuilds it from the
    // theme on restore. Only eligible parts contribute, matching totals/cart.
    const barCuts = {};
    for (const [partName, sel] of this.#barCuts) {
      if (!this.#barCutsEligible(partName)) continue;
      barCuts[partName] = {
        width:  sel.width?.variantId  || null,
        height: sel.height?.variantId || null,
        slit:   !!sel.slit,
      };
    }

    // Auto-add-to-cart-on-open is only offered when the build is actually
    // cart-ready; an incomplete build can still be shared, just without that option.
    this._shareModal.open({ parts, addOns, barCuts }, opener, this.#isBuildReady());
  }

  // The checkboxes are the single source of truth for add-on selection.
  #selectedAddonRows() {
    return [...this.querySelectorAll('.js-builder-addon-checkbox:checked')]
      .map((cb) => cb.closest('.js-builder-addon'));
  }

  #toggleAddon(checkbox) {
    const el = checkbox.closest('.js-builder-addon');
    if (el) el.classList.toggle('is-selected', checkbox.checked);
    this.#syncUI();
  }

  #onSelect(detail) {
    const { blockId, variantId, price, weight, title, image, builderImage, barCut, available } = detail;
    const part = this.querySelector(`.js-builder-part[data-part-name="${blockId}"]`);
    this.#state.set(blockId, { variantId, price, weight, title, image, builderImage, barCut, available });
    if (part) this.#populatePart(part, detail);
    // The bar changed, so any prior cut may now be out of range — reset and relock.
    this.#refreshBarCutsForPart(blockId, { reset: true });
    this.#syncUI();
  }

  #populatePart(part, data) {
    const discount   = Number(this.dataset.discount) || 0;
    const discounted = Math.round(data.price * (1 - discount / 100));

    part.classList.add('is-selected');
    part.querySelector('.js-builder-part-remove').hidden = false;

    const info = part.querySelector('.js-builder-part-selected-info');
    info.hidden = false;
    part.querySelector('.js-builder-part-product-name').textContent = data.title;
    part.querySelector('.js-builder-part-price-was').textContent    = formatMoney(data.price);
    part.querySelector('.js-builder-part-price-now').textContent    = formatMoney(discounted);

    const thumb = part.querySelector('.js-builder-part-thumb');
    thumb.hidden    = false;
    thumb.innerHTML = `<img src="${data.image}" alt="" width="56" height="56" loading="lazy">`;

    const soldOut = part.querySelector('.js-builder-part-sold-out');
    if (soldOut) soldOut.hidden = data.available !== false;
  }

  #removePart(part) {
    this.#state.delete(part.dataset.partName);
    this.#clearPart(part);
    this.#refreshBarCutsForPart(part.dataset.partName, { reset: true });
    this.#syncUI();
  }

  // ── Bar cuts ────────────────────────────────────────────────────────────────

  #barCutsCardsFor(partName) {
    return [...this.querySelectorAll('.js-builder-bar-cuts-card')]
      .filter((card) => card.dataset.linkedPart === partName);
  }

  // Whether the part is selected with a variant whose metafields allow any cut
  // (width, height) or the slit add-on.
  #barCutsEligible(partName) {
    const bounds = this.#state.get(partName)?.barCut;
    return !!bounds && (bounds.widthEnabled || bounds.heightEnabled || bounds.slitEnabled);
  }

  #openBarCutsModal(card, opener) {
    if (!card || !this._barCutsModal) return;
    const partName = card.dataset.linkedPart;
    const bounds   = this.#state.get(partName)?.barCut;
    if (!bounds) return;

    const sel = this.#barCuts.get(partName) || {};
    this._barCutsModal.open({
      linkedPart: partName,
      bounds,
      selection: {
        width:  sel.width?.variantId  || null,
        height: sel.height?.variantId || null,
        slit:   !!sel.slit,
      },
      labels: {
        title:   card.dataset.modalTitle   || '',
        confirm: card.dataset.confirmLabel || '',
        none:    card.dataset.noneLabel    || '',
        empty:   card.dataset.unavailablePrompt || '',
      },
      opener,
    });
  }

  #onBarCutsSelect({ linkedPart, cuts }) {
    const width  = cuts.width  || null;
    const height = cuts.height || null;
    const slit   = cuts.slit   || null;
    if (width || height || slit) this.#barCuts.set(linkedPart, { width, height, slit });
    else this.#barCuts.delete(linkedPart);
    this.#barCutsCardsFor(linkedPart).forEach((card) => this.#renderBarCutsCard(card));
    this.#syncUI();
  }

  #clearBarCuts(card) {
    this.#barCuts.delete(card.dataset.linkedPart);
    this.#renderBarCutsCard(card);
    this.#syncUI();
  }

  #refreshBarCutsForPart(partName, { reset = false } = {}) {
    const cards = this.#barCutsCardsFor(partName);
    if (!cards.length) return;
    if (reset) this.#barCuts.delete(partName);
    cards.forEach((card) => this.#renderBarCutsCard(card));
  }

  #refreshAllBarCuts() {
    this.querySelectorAll('.js-builder-bar-cuts-card')
      .forEach((card) => this.#renderBarCutsCard(card));
  }

  // Reflects locked / not-available / active / selected states onto one card.
  #renderBarCutsCard(card) {
    const partName = card.dataset.linkedPart;
    const part     = this.#state.get(partName);
    const eligible = this.#barCutsEligible(partName);
    const sel      = this.#barCuts.get(partName);
    const hasSel   = !!sel && (sel.width || sel.height || sel.slit);
    const selected = eligible && hasSel;

    const trigger = card.querySelector('.js-builder-bar-cuts-trigger');
    const prompt  = card.querySelector('.js-builder-bar-cuts-prompt');
    const info    = card.querySelector('.js-builder-bar-cuts-info');
    const remove  = card.querySelector('.js-builder-bar-cuts-remove');
    const summary = card.querySelector('.js-builder-bar-cuts-summary');
    const priceEl = card.querySelector('.js-builder-bar-cuts-price');

    card.classList.toggle('is-disabled', !eligible);
    card.classList.toggle('is-selected', selected);
    if (trigger) trigger.disabled = !eligible;
    if (remove)  remove.hidden = !selected;
    if (info)    info.hidden   = !selected;
    if (prompt)  prompt.hidden = selected;

    if (selected) {
      const titles = [];
      let total = 0;
      for (const cut of [sel.width, sel.height, sel.slit]) {
        if (cut) { titles.push(cut.title); total += cut.price; }
      }
      if (summary) summary.textContent = titles.join(' · ');
      if (priceEl) priceEl.textContent = formatMoney(total);
    } else if (prompt) {
      prompt.textContent = !part
        ? (card.dataset.disabledPrompt || '')
        : eligible
          ? (card.dataset.activePrompt || '')
          : (card.dataset.unavailablePrompt || '');
    }
  }

  #clearPart(part) {
    part.classList.remove('is-selected');
    part.querySelector('.js-builder-part-remove').hidden = true;

    const info = part.querySelector('.js-builder-part-selected-info');
    info.hidden = true;

    const thumb = part.querySelector('.js-builder-part-thumb');
    thumb.hidden    = true;
    thumb.innerHTML = '';

    const soldOut = part.querySelector('.js-builder-part-sold-out');
    if (soldOut) soldOut.hidden = true;
  }

  // A build is ready (add-to-cartable) once it meets the minimum part count and
  // has no unavailable selections. Shared by the ATC button and the share modal.
  #isBuildReady() {
    const minParts = Number(this.dataset.minParts) || 0;
    if (this.#state.size < minParts) return false;
    return ![...this.#state.values()].some(v => v.available === false);
  }

  #syncUI() {
    const selected = this.#state.size;
    const total    = this.querySelectorAll('.js-builder-part').length;
    const discount = Number(this.dataset.discount) || 0;

    this._count.textContent = selected;
    this._fill.style.width  = total ? `${(selected / total) * 100}%` : '0%';
    this._track.setAttribute('aria-valuenow', selected);

    let totalPrice         = 0;
    let totalOriginalPrice = 0;
    let totalWeight        = 0;
    const previewLayers = [];
    const allParts      = Array.from(this.querySelectorAll('.js-builder-part'));

    for (const [blockId, item] of this.#state) {
      totalOriginalPrice += item.price;
      totalPrice         += Math.round(item.price * (1 - discount / 100));
      totalWeight        += item.weight;
      if (item.builderImage) {
        const part     = this.querySelector(`.js-builder-part[data-part-name="${blockId}"]`);
        const domIndex = part ? allParts.indexOf(part) : Infinity;
        const attr     = part?.dataset.layerOrder;
        const order    = attr != null && attr !== '' ? Number(attr) : domIndex;
        previewLayers.push({ src: item.builderImage, order });
      }
    }

    // Add-ons — full price (no discount), so they add equally to was/now totals
    for (const el of this.#selectedAddonRows()) {
      const price  = Number(el.dataset.price)  || 0;
      const weight = Number(el.dataset.weight) || 0;
      totalOriginalPrice += price;
      totalPrice         += price;
      totalWeight        += weight;
    }

    // Bar cuts — also full price; only counted while their part is still eligible.
    for (const [partName, sel] of this.#barCuts) {
      if (!this.#barCutsEligible(partName)) continue;
      for (const cut of [sel.width, sel.height, sel.slit]) {
        if (!cut) continue;
        totalOriginalPrice += cut.price;
        totalPrice         += cut.price;
        totalWeight        += cut.weight;
      }
    }

    previewLayers.sort((a, b) => a.order - b.order);

    this._priceWas.textContent = formatMoney(totalOriginalPrice);
    this._priceWas.hidden      = totalOriginalPrice <= totalPrice;
    this._price.textContent    = formatMoney(totalPrice);
    this._weight.textContent   = `${totalWeight.toFixed(1)} ${(this.dataset.weightUnit || 'kg').toUpperCase()}`;
    this._atcBtn.disabled = !this.#isBuildReady();

    // Update preview panel — layer all parts that have a builder image
    // Render smaller image on mobile to prevent site being borked on Safari after load
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (this._preview) {
      this._preview.innerHTML = previewLayers
        .map(({ src }) => {
          const srcUrl = new URL(src.indexOf('//') === 0 ? src.replace('//', 'https://') : src);
          srcUrl.searchParams.set('width', isMobile ? '768' : '2048');
          return `<img src="${srcUrl.toString()}" alt="" aria-hidden="true" class="scooter-builder__preview-layer">`
        })
        .join('');
    }

    this.#saveToStorage();
  }

  async #addToCart() {
    // One shared id per add-to-cart, stamped on every line item so the whole
    // build (parts + add-ons + cuts) can be grouped together in the cart.
    const bundleId = crypto?.randomUUID?.()
      ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const partItems = Array.from(this.#state.entries()).map(([partName, { variantId }]) => ({
      id: Number(variantId),
      quantity: 1,
      properties: { [BUILDER_PART_ATTRIBUTE]: partName, [BUNDLE_ATTRIBUTE]: bundleId },
    }));

    // Add-ons are plain line items — no builder_part property, so the bundle
    // discount function leaves them at full price.
    const addonItems = this.#selectedAddonRows().map((el) => ({
      id: Number(el.dataset.variantId),
      quantity: 1,
      properties: { [BUNDLE_ATTRIBUTE]: bundleId },
    }));

    // Bar cuts — plain line items too (full price), one per chosen dimension.
    const barCutItems = [];
    for (const [partName, sel] of this.#barCuts) {
      if (!this.#barCutsEligible(partName)) continue;
      for (const cut of [sel.width, sel.height, sel.slit]) {
        if (cut) {
          barCutItems.push({
            id: Number(cut.variantId),
            quantity: 1,
            properties: { [BUNDLE_ATTRIBUTE]: bundleId },
          });
        }
      }
    }

    const items = [...partItems, ...addonItems, ...barCutItems];

    this._atcBtn.setAttribute('aria-busy', 'true');
    this._atcBtn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) throw new Error('Cart add failed');

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
        detail: { opener: this._atcBtn },
      }));
    } catch (err) {
      console.error('[ScooterBuilder] Add to cart failed:', err);
    } finally {
      this._atcBtn.removeAttribute('aria-busy');
      this.#syncUI();
    }
  }

  #reset() {
    this.#state.clear();
    this.#barCuts.clear();
    this.querySelectorAll('.js-builder-part').forEach((part) => this.#clearPart(part));
    this.querySelectorAll('.js-builder-addon').forEach((el) => {
      el.classList.remove('is-selected');
      const cb = el.querySelector('.js-builder-addon-checkbox');
      if (cb) cb.checked = false;
    });
    this.#refreshAllBarCuts();
    this.#syncUI();
  }

  async #savePreviewImage(btn) {
    btn?.setAttribute('aria-busy', 'true');
    try {
      const SIZE = 1000;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = SIZE;
      const ctx = canvas.getContext('2d');

      const layers = Array.from(
        this.querySelector('.scooter-builder__preview-inner')
            .querySelectorAll('img.scooter-builder__preview-layer')
      );

      for (const layer of layers) {
        await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload  = () => { ctx.drawImage(img, 0, 0, SIZE, SIZE); resolve(); };
          img.onerror = reject;
          img.src = layer.src;
        });
      }

      await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          Object.assign(document.createElement('a'), { href: url, download: 'scooter-build.png' }).click();
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png');
      });
    } catch (err) {
      console.error('[ScooterBuilder] Save image failed:', err);
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  }

  // ── SessionStorage persistence ────────────────────────────────────────────

  get #storageKey() {
    return `scooter-builder:${this.dataset.sectionId}`;
  }

  // Single key holds the whole selection: { parts: {blockId: data}, addons: [id] }.
  #loadFromStorage() {
    try {
      const raw = sessionStorage.getItem(this.#storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  #saveToStorage() {
    try {
      sessionStorage.setItem(this.#storageKey, JSON.stringify({
        parts:   Object.fromEntries(this.#state),
        addons:  this.#selectedAddonRows().map((el) => el.dataset.variantId),
        barCuts: Object.fromEntries(this.#barCuts),
      }));
    } catch {
      // Storage unavailable (private browsing, quota exceeded) — fail silently
    }
  }

  #restoreFromStorage() {
    const data = this.#loadFromStorage();
    if (!data) return;
    this.#applySelection(data.parts);
    this.#applyAddons(data.addons);
    this.#applyBarCuts(data.barCuts);
    this.#refreshAllBarCuts();
  }

  // The slit is a single fixed product fully described by the modal markup, so we
  // rebuild its cut from that baked data rather than trusting the stored/remote
  // payload — keeping the label, price and weight in sync with the live theme.
  // Callers only need a truthy `slit` flag (and the bar's slitEnabled bound).
  #slitCutFromDom() {
    const input = this._barCutsModal?.querySelector('.js-builder-bar-cuts-slit');
    if (!input) return null;
    return {
      variantId: input.dataset.variantId,
      price:     Number(input.dataset.price)  || 0,
      weight:    Number(input.dataset.weight) || 0,
      title:     input.dataset.title || '',
    };
  }

  // Re-applies stored cuts, but only for a part that is selected and still
  // eligible — so a stale cut can never survive a bar that no longer allows it.
  #applyBarCuts(barCuts) {
    if (!barCuts || typeof barCuts !== 'object' || Array.isArray(barCuts)) return;
    for (const [partName, sel] of Object.entries(barCuts)) {
      if (!sel || !this.#barCutsEligible(partName)) continue;
      const width  = sel.width  || null;
      const height = sel.height || null;
      // Slit is reconstructed from the theme; the payload's value is just a flag,
      // and the bar must actually allow a slit before we restore one.
      const slit = sel.slit && this.#state.get(partName)?.barCut?.slitEnabled
        ? this.#slitCutFromDom()
        : null;
      if (width || height || slit) this.#barCuts.set(partName, { width, height, slit });
    }
  }

  #applySelection(selection) {
    if (!selection || typeof selection !== 'object' || Array.isArray(selection)) return;
    for (const [blockId, data] of Object.entries(selection)) {
      const part = this.querySelector(`.js-builder-part[data-part-name="${blockId}"]`);
      if (!part || !data?.variantId) continue;
      this.#state.set(blockId, data);
      this.#populatePart(part, data);
    }
  }

  // Checks add-on boxes by variant ID; #syncUI re-derives totals from the DOM,
  // so the stored/remote payload only needs the IDs and can't carry stale figures.
  #applyAddons(variantIds) {
    if (!Array.isArray(variantIds)) return;
    for (const variantId of variantIds) {
      const el = this.querySelector(`.js-builder-addon[data-variant-id="${variantId}"]`);
      if (!el) continue;
      el.classList.add('is-selected');
      const cb = el.querySelector('.js-builder-addon-checkbox');
      if (cb) cb.checked = true;
    }
  }

  // ── API loading ───────────────────────────────────────────────────────────

  async #loadFromApi(buildId) {
    this.#showLoading();
    try {
      const domain = window.Shopify?.shop ?? '';
      const url = `${API_BASE_URL}/builds/${encodeURIComponent(buildId)}?domain=${encodeURIComponent(domain)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const json = await res.json();
      this.#applySelection(json.parts);
      this.#applyAddons(json.addOns ?? []);
      this.#applyBarCuts(json.barCuts);
      this.#refreshAllBarCuts();
    } catch (err) {
      console.error('[ScooterBuilder] Failed to load build from API:', err);
      this.#restoreFromStorage();
    } finally {
      this.#hideLoading();
      this.#syncUI();
    }

    // Shared links can request auto add-to-cart via ?atc=true. Fire it only once
    // the build has hydrated, and only if something actually loaded.
    const wantsAtc = new URLSearchParams(location.search).get('atc') === 'true';
    const hasItems = Object.values(this.#state).every(v => v.available === true);
    if (wantsAtc && hasItems) {
      // Drops the atc flag from the URL (keeping buildId) so refreshing a shared
      // link reloads the build without adding to cart again.
      // const url = new URL(location.href);
      // url.searchParams.delete('atc');
      // history.replaceState(null, '', url);

      // Add to cart
      this.#addToCart();
    }
  }

  #showLoading() {
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    this._loading?.removeAttribute('aria-hidden');
  }

  #hideLoading() {
    this.classList.remove('is-loading');
    this.removeAttribute('aria-busy');
    this._loading?.setAttribute('aria-hidden', 'true');
  }
}

customElements.define('scooter-builder', ScooterBuilder);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

function formatMoney(cents) {
  try {
    return (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'AUD',
      minimumFractionDigits: 2,
    });
  } catch (_) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}
